// 操作 tab 的权限范围判定纯函数：迁移自 src/views/Profile/VibeProjectList.js，
// 供 OperationsPanel 与「申请权限」弹窗共用；Profile 原有逻辑不改，避免回归。
//
// 2026-11-04：项目授权合并后，数据库读写 = 数据库读写 + GitLab 代码仓库 Maintainer，
// 「代码仓库权限」不再是可单独申请的独立范围；hasCodePermission 只保留用于展示。
// 数据来源（AIC 详情接口，后端的详情权限标记）：
//   mysqlDatabaseName / apiConfigured / hasDatabasePermission / hasApiPermission
//   / hasCodePermission / hasPendingRequest / hasPermission
// 新后端一定会下发这些键：无数据库时 mysqlDatabaseName 为 null，未配置接口时 apiConfigured=false。
// 老后端未部署时这些键不存在，此时降级为上一版行为（AIC 数据集默认数据库/接口可申请），
// 真正的可用性仍由后端 applyProjectPermission 二次校验。

export function isTrue (value) {
  return value === true || value === 1 || value === '1' || value === 'true'
}

// 区分“字段缺失（老后端）”与“字段存在但值为 null/false（新后端）”。
export function hasProvidedField (source, key) {
  return Boolean(source) && Object.prototype.hasOwnProperty.call(source, key) && source[key] !== undefined
}

// AIC 数据集项目不再创建/使用 GitLab 仓库：代码仓库维度对数据集不适用。
// 项目授权合并后代码仓库跟随数据库读写，本函数只用于文案与选项显隐；
// 数据集不参与可申请范围，也不参与「已有全部权限」判定（见 getPermissionScopeAvailability）。
export function isCodePermissionApplicable (project) {
  const source = project || {}
  return (source.projectKind || 'dataset') !== 'dataset'
}

// 负责人在建项目时已是 GitLab Maintainer，不写入成员账本，展示时按 Owner 合并。
export function hasCodePermission (project) {
  return isTrue(project && project.hasCodePermission)
    || isTrue(project && project.codePermissionGranted)
    || isTrue(project && project.isOwner)
}

export function isDatabaseConfigured (project) {
  const source = project || {}
  // 老后端没有该字段：AIC 数据集按“有库”降级，避免整块申请入口消失。
  if (!hasProvidedField(source, 'mysqlDatabaseName')) {
    return (source.projectKind || 'dataset') !== 'application'
  }
  return Boolean(String(source.mysqlDatabaseName || '').trim())
}

export function isApiConfigured (project) {
  const source = project || {}
  if (!hasProvidedField(source, 'apiConfigured')) {
    return (source.projectKind || 'dataset') !== 'application'
  }
  return isTrue(source.apiConfigured)
}

// 返回「当前还能申请」的范围：已拥有/未配置的范围返回 false（弹窗里对应选项置灰）。
export function getPermissionScopeAvailability (project) {
  const source = project || {}
  const databaseConfigured = isDatabaseConfigured(source)
  const apiConfigured = isApiConfigured(source)
  const database = databaseConfigured && !isTrue(source.hasDatabasePermission)
  const api = apiConfigured && !isTrue(source.hasApiPermission)
  return {
    database,
    api,
    // 全部权限 = 数据库读写（含代码仓库） + 接口。
    all: databaseConfigured && apiConfigured && (database || api)
  }
}

// 已缺少（还没拿到）的权限范围；用于给「申请权限」弹窗选默认值。
export function getMissingPermissionScopes (project) {
  const availability = getPermissionScopeAvailability(project)
  const scopes = []
  if (availability.database) scopes.push('database')
  if (availability.api) scopes.push('api')
  return scopes
}

// 是否已拥有全部可申请权限：没有任何可申请范围时面板展示「已有全部权限」并禁用申请。
// 合并授权后只有数据库与接口两个维度；代码仓库跟随数据库读写。
export function hasAllPermissions (project) {
  const availability = getPermissionScopeAvailability(project || {})
  return !availability.database && !availability.api && !availability.all
}

export function getRecommendedPermissionScope (project) {
  const scopes = getMissingPermissionScopes(project)
  if (scopes.includes('database') && scopes.includes('api')) return 'all'
  return scopes[0] || 'database'
}

export function getDefaultPermissionScope (project) {
  const availability = getPermissionScopeAvailability(project)
  const recommended = getRecommendedPermissionScope(project)
  if (availability[recommended]) return recommended
  if (availability.database) return 'database'
  if (availability.api) return 'api'
  return 'database'
}

// projectKind 可选：AIC 数据集没有代码仓库，读写范围文案不再声称含代码仓库。
export function getPermissionScopeText (scope, databasePermissionType, projectKind) {
  const codeApplicable = isCodePermissionApplicable({ projectKind })
  const databaseText = databasePermissionType === 'readonly'
    ? '数据库只读权限'
    : (codeApplicable ? '数据库读写权限（含代码仓库）' : '数据库读写权限')
  if (scope === 'api') return '接口权限'
  if (scope === 'all') return '全部权限（数据库读写 + 接口）'
  return databaseText
}

export function formatPermissionRequestTime (value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  const pad = number => (number < 10 ? `0${number}` : String(number))
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}
